import type { ReactNode } from 'react';
import { Button } from '~/components/ui/button';

interface FormActionsProps {
  onDiscard?: () => void;
  /** Renders Discard / Cancel as a link instead of a button. */
  discardHref?: string;
  discardLabel?: string;
  /** Disables Discard without a pending save, e.g. nothing has changed yet. */
  discardDisabled?: boolean;
  /** Omitted: the save button is `type="submit"` and the enclosing form handles it. */
  onSave?: () => void;
  saveLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  disabled?: boolean;
  /** Becomes the Save `title` while `disabled`, so a dead button explains itself. */
  disabledReason?: string;
  /** Id of the `<form>` Save submits, for actions rendered outside it (a modal footer). */
  form?: string;
  saveTestId?: string;
  discardTestId?: string;
  /** Rendered before Discard (a form's secondary actions). */
  children?: ReactNode;
}

/** The one Save / Discard row (DESIGN.md §4.9): right-aligned, outline then primary, both `sm`. */
export function FormActions({
  onDiscard,
  discardHref,
  discardLabel = 'Discard',
  discardDisabled = false,
  onSave,
  saveLabel = 'Save changes',
  busyLabel = 'Saving…',
  busy = false,
  disabled = false,
  disabledReason,
  form,
  saveTestId,
  discardTestId,
  children,
}: FormActionsProps) {
  return (
    <div className="flex items-center justify-end gap-3">
      {children}
      {(onDiscard || discardHref) && (
        <Button
          variant="outline"
          size="sm"
          onClick={onDiscard && (() => onDiscard())}
          href={discardHref}
          disabled={busy || discardDisabled}
          testId={discardTestId}
        >
          {discardLabel}
        </Button>
      )}
      <Button
        variant="primary"
        size="sm"
        form={form}
        type={onSave ? 'button' : 'submit'}
        onClick={onSave && (() => onSave())}
        disabled={busy || disabled}
        title={disabled ? disabledReason : undefined}
        testId={saveTestId}
      >
        {busy ? busyLabel : saveLabel}
      </Button>
    </div>
  );
}
